import { format } from 'date-fns/format';
import { isThisYear } from 'date-fns/isThisYear';
import { useRef, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { type ModalFC } from '@/components/modals/modalsManager';
import { Dialog } from '@/components/ui/dialog';
import { IconButton } from '@/components/ui/IconButton';
import { XStack, YStack } from '@/components/ui/layout/layout';

import { CHANGELOG_URL, type ChangelogEntry } from './changelog.feed';
import { useChangelogFeedQuery } from './changelog.query';
import { ChangelogEntryContent } from './ChangelogEntryContent';
import './changelog-entry.css';

export const ChangelogEntryModal: ModalFC<{ entry: ChangelogEntry }> = ({
  open,
  onOpenChange,
  entry,
}) => {
  const { data: entries = [entry] } = useChangelogFeedQuery();
  const [entryId, setEntryId] = useState(entry.id);
  const scrollRef = useRef<HTMLDivElement>(null);

  const entryIndex = entries.findIndex(({ id }) => id === entryId);
  const currentEntry = entries[entryIndex] ?? entry;
  const olderEntry = entryIndex >= 0 ? entries[entryIndex + 1] : undefined;
  const newerEntry = entryIndex > 0 ? entries[entryIndex - 1] : undefined;

  const showEntry = (nextEntry: ChangelogEntry) => {
    setEntryId(nextEntry.id);
    scrollRef.current?.scrollTo({ top: 0 });
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content
        showCloseButton={false}
        aria-describedby={undefined}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          scrollRef.current?.focus();
        }}
        className="flex h-[calc(100vh-4rem)] max-h-[60rem] max-w-2xl flex-col gap-0 overflow-hidden p-0"
      >
        <XStack className="h-12 shrink-0 items-center border-b border-border px-6 text-sm">
          <XStack className="flex-1 items-center gap-0.5">
            <IconButton
              icon={Icons.chevronLeft}
              size="xs"
              tooltip="Older update"
              disabled={!olderEntry}
              onClick={() => olderEntry && showEntry(olderEntry)}
            />
            <time
              dateTime={format(currentEntry.publishedAt, 'yyyy-MM-dd')}
              className="min-w-[3.25rem] text-center tabular-nums text-foreground-muted"
            >
              {format(
                currentEntry.publishedAt,
                isThisYear(currentEntry.publishedAt) ? 'MMM d' : 'MMM d, yyyy'
              )}
            </time>
            <IconButton
              icon={Icons.chevronRight}
              size="xs"
              tooltip="Newer update"
              disabled={!newerEntry}
              onClick={() => newerEntry && showEntry(newerEntry)}
            />
          </XStack>
          <a
            href={CHANGELOG_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 rounded-md px-2 py-1 font-medium text-foreground transition-colors hover:bg-surface-hover"
          >
            Changelog
            <Icons.arrowUpRight className="size-3.5 text-foreground-muted" />
          </a>
          <XStack className="flex-1 justify-end">
            <Dialog.Close className="bg-transparent p-1.5 text-foreground-muted hover:bg-surface-hover hover:text-foreground" />
          </XStack>
        </XStack>
        <YStack
          ref={scrollRef}
          tabIndex={-1}
          className="min-h-0 flex-1 overflow-y-auto px-8 pt-7 pb-10 outline-none"
        >
          <Dialog.Title className="font-serif text-3xl font-normal leading-tight">
            {currentEntry.title}
          </Dialog.Title>
          <ChangelogEntryContent entry={currentEntry} />
        </YStack>
      </Dialog.Content>
    </Dialog.Root>
  );
};
